/**
 * Catálogo de serviços.
 *
 * O tempo padrão daqui é só o palpite inicial: vale para quem ainda não tem
 * histórico na casa. Assim que o barbeiro souber que aquele cliente foge do
 * padrão, o tempo dele passa a viver na ficha do cliente — que é onde a
 * agenda vai buscar.
 */

import type { ServiceView } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../../App.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Sheet,
  Spinner,
} from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { duration, money } from '../../lib/format.ts';

/** Cores que separam os serviços na linha do tempo. */
const COLORS = ['#C9A227', '#0E5A6B', '#8B3A3A', '#4A7C59', '#5B5B7A', '#B5651D'];

export function Services() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<ServiceView | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
  });

  const services = data?.services ?? [];
  const active = services.filter((service) => service.active);
  const inactive = services.filter((service) => !service.active);

  return (
    <div className="flex min-h-full flex-col pb-28">
      <ScreenHeader
        title="Serviços"
        subtitle={`${active.length} ativos`}
        action={
          <button
            type="button"
            onClick={() => navigate('/ajustes')}
            className="touch-target shrink-0 px-2 text-[15px] font-medium text-ink-soft"
          >
            Voltar
          </button>
        }
      />

      {isPending && <Spinner />}

      {!isPending && services.length === 0 && (
        <EmptyState
          title="Nenhum serviço cadastrado"
          description="Comece pelos que você mais faz. Combos como “cabelo e barba” são um serviço próprio, não a soma de dois — é assim que o tempo de cada cliente fecha certo."
        />
      )}

      {active.length > 0 && (
        <ul className="px-4 pt-2">
          {active.map((service) => (
            <li key={service.id}>
              <button
                type="button"
                onClick={() => setEditing(service)}
                className="flex w-full items-center gap-3 border-b border-line py-3.5 text-left"
              >
                <span
                  className="h-9 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: service.color }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-ink">
                    {service.name}
                  </span>
                  <span className="block text-[13px] text-ink-faint">
                    {duration(service.defaultDurationMinutes)} · {money(service.priceCents)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {inactive.length > 0 && (
        <section className="px-4 pt-6">
          <h2 className="pb-1 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Desativados
          </h2>
          <ul>
            {inactive.map((service) => (
              <li key={service.id}>
                <button
                  type="button"
                  onClick={() => setEditing(service)}
                  className="flex w-full items-center justify-between gap-3 border-b border-line py-3 text-left opacity-60"
                >
                  <span className="truncate text-[15px] text-ink">{service.name}</span>
                  <Badge>inativo</Badge>
                </button>
              </li>
            ))}
          </ul>
          <p className="pt-2 text-[12px] text-ink-faint">
            Serviço não é excluído, é desativado: os atendimentos antigos apontam para ele e apagar
            reescreveria o histórico do faturamento.
          </p>
        </section>
      )}

      <div className="pb-safe pointer-events-none sticky bottom-0 z-40 px-4 pt-2">
        <Button full className="pointer-events-auto shadow-lg" onClick={() => setCreating(true)}>
          Novo serviço
        </Button>
      </div>

      {(creating || editing) && (
        <ServiceSheet
          service={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ServiceSheet({ service, onClose }: { service: ServiceView | null; onClose: () => void }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(service?.name ?? '');
  const [price, setPrice] = useState(
    service ? (service.priceCents / 100).toFixed(2).replace('.', ',') : '',
  );
  const [minutes, setMinutes] = useState(String(service?.defaultDurationMinutes ?? 30));
  const [color, setColor] = useState(service?.color ?? COLORS[0]);
  const [error, setError] = useState<ApiError | null>(null);

  const payload = () => ({
    name,
    priceCents: Math.round(Number(price.replace(/\./g, '').replace(',', '.')) * 100) || 0,
    defaultDurationMinutes: Number(minutes),
    color,
  });

  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ['services'] });
    await queryClient.invalidateQueries({ queryKey: ['tenant'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      service ? api.patch(`/services/${service.id}`, payload()) : api.post('/services', payload()),
    onSuccess: done,
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const toggleActive = useMutation({
    mutationFn: () =>
      service?.active
        ? api.delete(`/services/${service.id}`)
        : api.patch(`/services/${service?.id}`, { active: true }),
    onSuccess: done,
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={service ? service.name : 'Novo serviço'}
      footer={
        <Button full loading={save.isPending} onClick={() => save.mutate()}>
          Salvar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Nome"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoCapitalize="sentences"
          placeholder="Cabelo e barba"
          error={error?.fieldError('name')}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Preço"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            inputMode="decimal"
            placeholder="70,00"
            error={error?.fieldError('priceCents')}
          />
          <Field
            label="Tempo padrão"
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
            type="number"
            inputMode="numeric"
            min={5}
            max={480}
            step={5}
            hint="em minutos"
            error={error?.fieldError('defaultDurationMinutes')}
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-ink-soft">Cor na agenda</p>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((option) => (
              <button
                key={option}
                type="button"
                aria-label={`Cor ${option}`}
                onClick={() => setColor(option)}
                className={`h-11 w-11 rounded-xl border-2 ${
                  color === option ? 'border-ink' : 'border-transparent'
                }`}
                style={{ backgroundColor: option }}
              />
            ))}
          </div>
        </div>

        <p className="rounded-xl border border-line bg-surface-raised px-4 py-3 text-[13px] text-ink-soft">
          Este é o tempo de quem ainda não tem histórico. Para um cliente específico, ajuste na
          ficha dele — é o tempo de lá que a agenda usa.
        </p>

        {error && <ErrorNote>{error.message}</ErrorNote>}

        {service && (
          <Button
            variant={service.active ? 'danger' : 'secondary'}
            full
            loading={toggleActive.isPending}
            onClick={() => toggleActive.mutate()}
          >
            {service.active ? 'Desativar serviço' : 'Reativar serviço'}
          </Button>
        )}
      </div>
    </Sheet>
  );
}
